import { DATA_SOURCE_IDS } from './dataInputModel';

const NORMAL_RECALCULATION_SKIPS = new Set([
  'SKIPPED_NO_CHANGE',
  'SKIPPED_NO_AFFECTED_DATA',
  'SKIPPED_NO_RECALCULATION_REQUIRED',
  'SKIPPED_NOOP_REIMPORT',
]);

const ACTIONABLE_RECALCULATION_KEYS = Object.freeze({
  SKIPPED_MISSING_EMISSION_FACTOR:
    'dataInput.recalculation.sourceSavedMissingEmissionFactor',
  SKIPPED_MISSING_SCOPE2_ASSIGNMENT:
    'dataInput.recalculation.sourceSavedMissingAssignment',
  SKIPPED_MISSING_SOURCE:
    'dataInput.importResult.sourceSavedMissingSource',
  SKIPPED_INCOMPLETE_EF:
    'dataInput.importResult.sourceSavedIncompleteEmissionFactor',
});

const GENERIC_COUNTER_GROUPS = Object.freeze([
  {
    key: 'rows',
    counters: ['total_rows', 'processed_rows', 'included_rows'],
  },
  {
    key: 'changes',
    counters: ['inserted_rows', 'updated_rows', 'unchanged_rows', 'deleted_rows'],
  },
  {
    key: 'issues',
    counters: [
      'skipped_rows', 'invalid_rows', 'ambiguous_rows', 'filtered_rows',
      'unclassified_rows',
    ],
  },
]);

const WEEE_ISSUE_COUNTERS = Object.freeze([
  'skipped_rows', 'invalid_rows', 'ambiguous_rows', 'filtered_rows',
  'unclassified_rows',
]);

const SCOPE3_AF_ROW_COUNTERS = Object.freeze(['total_rows', 'included_rows']);
const SCOPE3_AF_ISSUE_COUNTERS = Object.freeze([
  'skipped_rows', 'duplicate_rows', 'ambiguous_rows',
]);

const MESSAGE_TOKEN = /^[A-Z][A-Z0-9_]*$/;
const normalized = value => String(value || '').toUpperCase();
const finiteCounter = value => Number.isFinite(Number(value));
const counterValue = value => Number(value);

const descriptor = (severity, messageKey, options = {}) => ({
  severity,
  messageKey: messageKey || null,
  messageCode: options.messageCode || null,
  params: options.params || {},
  causeCode: options.causeCode || null,
});

// A partial import's own root message is already covered by the descriptor;
// only surface an underlying cause code when it adds new information.
const causeCodeOf = result => (
  MESSAGE_TOKEN.test(result.message || '') && result.message !== 'IMPORT_PROCESSING_FAILED'
    ? result.message
    : null
);

/**
 * Return one complete, localizable outcome for an import. Consumers must render
 * either messageKey or messageCode once; neither value is a sentence fragment.
 */
export function getImportResultPresentation(result) {
  if (!result) return null;

  const status = normalized(result.status);
  const isPartial = status === 'PARTIAL'
    || Boolean(result.partialDataPossible)
    || result.recoveryAction === 'REIMPORT';
  if (isPartial) {
    return descriptor(
      'warning',
      result.recoveryAction === 'REIMPORT'
        ? 'dataInput.history.partialDataReimport'
        : 'dataInput.history.partialDataPossible',
      {
        params: result.params || {},
        causeCode: causeCodeOf(result),
      },
    );
  }
  if (status === 'CANCELLED' || result.message === 'IMPORT_CANCELLED') {
    return descriptor('info', 'dataInput.importDialog.cancelled');
  }
  if (status === 'FAILED') {
    return descriptor('error', null, {
      messageCode: MESSAGE_TOKEN.test(result.message || '')
        ? result.message
        : 'IMPORT_PROCESSING_FAILED',
      params: result.params || {},
    });
  }
  if (status !== 'SUCCESS') return null;

  const recalculation = result.recalculation;
  const outcome = normalized(recalculation?.outcome);
  const reasonCode = recalculation?.reasonCode || recalculation?.reason_code || null;
  if (!outcome) return descriptor('success', 'common.feedback.import.success');
  if (outcome === 'SUCCESS') {
    return descriptor('success', 'dataInput.importResult.successRecalculated');
  }
  if (outcome === 'FAILED') {
    return descriptor('error', 'dataInput.recalculation.sourceSavedCalculationFailed');
  }
  if (outcome === 'SKIPPED') {
    if (ACTIONABLE_RECALCULATION_KEYS[reasonCode]) {
      return descriptor('warning', ACTIONABLE_RECALCULATION_KEYS[reasonCode]);
    }
    if (NORMAL_RECALCULATION_SKIPS.has(reasonCode)) {
      return descriptor('success', 'dataInput.importResult.successNoRecalculationNeeded');
    }
    return descriptor('warning', 'dataInput.importResult.successRecalculationSkipped');
  }

  return descriptor('success', 'common.feedback.import.success');
}

export function getWeeeAnnualValueState(counters = {}) {
  if (counterValue(counters.inserted_rows) > 0) return 'ADDED';
  if (counterValue(counters.updated_rows) > 0) return 'UPDATED';
  if (counterValue(counters.unchanged_rows) > 0) return 'UNCHANGED';
  return null;
}

const genericCounterGroups = counters => GENERIC_COUNTER_GROUPS.map(group => ({
  key: group.key,
  values: group.counters
    .filter(key => finiteCounter(counters[key]))
    .map(key => ({
      key,
      labelKey: `dataInput.counter.${key}`,
      value: counterValue(counters[key]),
      valueKey: null,
    })),
})).filter(group => group.values.length > 0);

const weeeCounterGroups = (counters) => {
  const sourceValues = finiteCounter(counters.total_rows)
    ? counterValue(counters.total_rows)
    : finiteCounter(counters.processed_rows)
      ? counterValue(counters.processed_rows)
      : finiteCounter(counters.included_rows)
        ? counterValue(counters.included_rows)
        : null;
  const annualState = getWeeeAnnualValueState(counters);
  const groups = [];
  if (sourceValues !== null) {
    groups.push({
      key: 'rows',
      values: [{
        key: 'source_values_checked',
        labelKey: 'dataInput.importResult.sourceValuesChecked',
        value: sourceValues,
        valueKey: null,
      }],
    });
  }
  if (annualState) {
    groups.push({
      key: 'changes',
      values: [{
        key: 'annual_weee_value',
        labelKey: 'dataInput.importResult.annualWeeeValue',
        value: null,
        valueKey: `dataInput.importResult.annualWeeeState.${annualState}`,
      }],
    });
  }
  const issues = WEEE_ISSUE_COUNTERS
    .filter(key => finiteCounter(counters[key]) && counterValue(counters[key]) > 0)
    .map(key => ({
      key,
      labelKey: `dataInput.counter.${key}`,
      value: counterValue(counters[key]),
      valueKey: null,
    }));
  if (issues.length > 0) groups.push({ key: 'issues', values: issues });
  return groups;
};

const scope3AfCounterGroups = (counters) => {
  const rows = SCOPE3_AF_ROW_COUNTERS
    .filter(key => finiteCounter(counters[key]))
    .map(key => ({
      key,
      labelKey: `dataInput.counter.${key}`,
      value: counterValue(counters[key]),
      valueKey: null,
    }));
  const aggregate = finiteCounter(counters.aggregate_count)
    ? [{
      key: 'aggregate_count',
      labelKey: 'dataInput.counter.aggregate_count',
      value: counterValue(counters.aggregate_count),
      valueKey: null,
    }]
    : [];
  const issues = SCOPE3_AF_ISSUE_COUNTERS
    .filter(key => finiteCounter(counters[key]) && counterValue(counters[key]) > 0)
    .map(key => ({
      key,
      labelKey: `dataInput.counter.${key}`,
      value: counterValue(counters[key]),
      valueKey: null,
    }));
  const groups = [];
  if (rows.length > 0) groups.push({ key: 'rows', values: rows });
  if (aggregate.length > 0) groups.push({ key: 'aggregate', values: aggregate });
  if (issues.length > 0) groups.push({ key: 'issues', values: issues });
  return groups;
};

export function getImportCounterGroups(sourceId, counters = {}) {
  if (sourceId === DATA_SOURCE_IDS.SCOPE_3_AF_REPORT) {
    return scope3AfCounterGroups(counters);
  }
  return sourceId === DATA_SOURCE_IDS.SCOPE_3_EWASTE
    ? weeeCounterGroups(counters)
    : genericCounterGroups(counters);
}
